import { useEffect, useMemo, useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { Input } from '@/client/components/ui/input';
import { Button } from '@/client/components/ui/button';
import { Label } from '@/client/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { useClientsStore } from '@/client/store/clientsStore';
import { API_BASE_URL } from '@/client/config';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'utilities/export-vtt',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: ExportVttComponent,
});

function firstOfMonth(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function todayISO(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function ExportVttComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const { clients, fetchClients, loading: clientsLoading } = useClientsStore();

  const [clientID, setClientID] = useState('');
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(todayISO());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const token = await getAccessTokenSilently();
      await fetchClients(token, logout);
    };
    if (clients.length === 0) load();
  }, [getAccessTokenSilently, fetchClients, logout, clients.length]);

  const activeClients = useMemo(
    () =>
      clients
        .filter((c) => c.active === 1)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [clients],
  );

  const selectedClient = clients.find((c) => c.clientID === clientID);
  const canSubmit = !!clientID && !!startDate && !!endDate && !downloading && startDate <= endDate;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({ clientID, startDate, endDate });
      const res = await fetch(`${API_BASE_URL}/api/vtt/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${body || res.statusText}`);
      }
      const total = Number(res.headers.get('X-Export-Total') ?? '0');
      const failed = Number(res.headers.get('X-Export-Failed') ?? '0');
      const blob = await res.blob();

      const disposition = res.headers.get('Content-Disposition') ?? '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] ?? `vtt-export_${clientID}_${startDate}_to_${endDate}.zip`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const ok = total - failed;
      if (failed > 0) {
        toast.warning(`Exported ${ok}/${total} captions (${failed} skipped)`);
      } else {
        toast.success(`Exported ${ok} caption${ok === 1 ? '' : 's'}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Export failed: ${msg}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-2xl font-semibold">Export VTT Manifest</h3>
          </div>
          <p className="text-muted-foreground mb-6">
            Produces a ZIP of every caption VTT bound to a <span className="font-medium">published</span> event
            for the selected client in the date range, plus a <code>manifest.csv</code> listing{' '}
            <code>eventID</code>, <code>dateCreated</code>, <code>playerUrl</code>, and <code>pathInZip</code>.
            Larger ranges may take a while — the server fetches every file before sending.
          </p>

          <div className="flex items-end gap-4 mb-4 flex-wrap">
            <div className="space-y-2 min-w-[280px]">
              <Label htmlFor="clientID">Client</Label>
              <Select value={clientID} onValueChange={setClientID} disabled={clientsLoading}>
                <SelectTrigger id="clientID">
                  <SelectValue placeholder={clientsLoading ? 'Loading clients...' : 'Select a client'} />
                </SelectTrigger>
                <SelectContent>
                  {activeClients.map((c) => (
                    <SelectItem key={c.clientID} value={c.clientID}>
                      {c.name}
                      <span className="text-muted-foreground ml-2 text-xs font-mono">{c.clientID}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="startDate">Start date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-[180px]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-[180px]"
              />
            </div>
            <div className="ml-auto">
              <Button onClick={handleDownload} disabled={!canSubmit}>
                <Download className="size-4 mr-2" />
                {downloading
                  ? 'Building ZIP...'
                  : selectedClient
                    ? `Download ZIP for ${selectedClient.name}`
                    : 'Download ZIP'}
              </Button>
            </div>
          </div>

          {startDate && endDate && startDate > endDate && (
            <p className="text-sm text-destructive mb-4">Start date must be on or before end date.</p>
          )}
        </section>
      </div>
    </Layout>
  );
}
